/**
 * Curated service catalog backing the Services & Secrets panel.
 *
 * This is a frontend-only, hand-curated MVP catalog grouped by *brokerability*
 * — molecule's frictionless differentiator. Each entry maps a backend secret
 * "category" (the value sent to `POST/DELETE /projects/:id/vault/broker`) to
 * the human-facing label, its brokerability class, the available providers,
 * and the env keys a bring-your-own-key (BYO) configuration writes via
 * `PUT /projects/:id/vault/:key`.
 *
 * **An `envKeys` entry must be an environment variable the app's own wiring
 * actually reads** — the registry's `secrets[].name` for the bond or the bond's
 * dependencies (the `@molecule/*-molecule` bonds read `MOLECULE_API_KEY`, the
 * ai-decisions/ocr LLM paths read the AI provider's key), or a documented
 * CONFIGURATION variable a keyless bond reads itself (the two OSM geocoding
 * bonds). A bond configured in CODE (sharp, puppeteer) has no env surface and
 * therefore gets no provider row: the panel writes these keys into the project
 * vault and marks the category BYO-configured from them, so a name nothing
 * reads is a control that cannot change the app while claiming it did.
 * `src/utilities/__tests__/services-catalog-keys.test.ts` is the gate — it
 * fails on any key outside that rule.
 *
 * **The mirror rule: every category the HOSTED services endpoint serves carries
 * the hosted row** — `{ id: 'molecule', label: 'Molecule (hosted)', envKeys:
 * ['MOLECULE_API_KEY'] }` — because that category's `@molecule/*-molecule` bond
 * reads `MOLECULE_API_KEY`, its registry secret. The panel can only NAME a key
 * (and only report the category BYO-configured from it, `byoMaskedForCategory`)
 * when the catalog lists it, so a served category without this row cannot
 * express a BYO configuration of the bond it is actually served by. The API's
 * `HOSTED_SERVICE_HANDLERS` declares each served category on its handler
 * (`category`), so the two lists are comparable — the same test derives them
 * and fails on a served category without the row.
 *
 * Brokerability classes:
 * - **A (Brokerable)** — molecule can broker its own keys for an instant,
 *   zero-config start; the user may switch to BYO at any time. Whether a
 *   class-A category is brokerable TODAY comes from the live
 *   `GET /projects/:id/services` `brokerSupported` flag (the chat pass-through
 *   and every hosted category) — the panel never offers "Use molecule's keys"
 *   without it.
 * - **C (BYO-only)** — legally/contractually must use the user's own
 *   credentials and is never brokered (payments, OAuth login, KYC, e-sign).
 *
 * Kept framework-free so the pure derivation helpers in
 * `services-utilities.ts` can be unit-tested in the app's node test env
 * (mirrors `utilities/activity.ts`).
 *
 * @module
 */

/** Brokerability class for a service category. */
export type ServiceClass = 'A' | 'C'

/** One provider option within a service category. */
export interface ServiceProvider {
  /** Stable provider id (also the prefix used for BYO env keys). */
  id: string
  /** Human-facing provider label. */
  label: string
  /**
   * Env keys a BYO configuration of this provider writes via
   * `PUT /vault/:key`. The first key is treated as the provider's primary
   * credential for "configured" detection.
   */
  envKeys: string[]
}

/** A curated service category. */
export interface ServiceCatalogEntry {
  /** Backend secret category (sent to the broker endpoints). */
  category: string
  /** Human-facing category label. */
  label: string
  /** Brokerability class. */
  serviceClass: ServiceClass
  /** Short one-line description of what the service does. */
  description: string
  /** For class C: a short note explaining why BYO is required. */
  byoReason?: string
  /** Available providers for this category. */
  providers: ServiceProvider[]
}

/**
 * The curated catalog. Order is the display order. Brokerable (class A)
 * categories come first, then BYO-only (class C).
 */
export const SERVICE_CATALOG: readonly ServiceCatalogEntry[] = [
  // --- Class A: Brokerable -------------------------------------------------
  {
    category: 'ai',
    label: 'AI',
    serviceClass: 'A',
    description: 'Large language models for chat, completion, and agents.',
    providers: [
      { id: 'anthropic', label: 'Anthropic', envKeys: ['ANTHROPIC_API_KEY'] },
      { id: 'openai', label: 'OpenAI', envKeys: ['OPENAI_API_KEY'] },
    ],
  },
  {
    category: 'embeddings',
    label: 'Embeddings',
    serviceClass: 'A',
    description: 'Vector embeddings for search and retrieval.',
    providers: [
      // The hosted bond (@molecule/api-ai-embeddings-molecule) — see the
      // "hosted row" rule in the module docstring.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      { id: 'openai', label: 'OpenAI', envKeys: ['OPENAI_API_KEY'] },
    ],
  },
  {
    category: 'image-generation',
    label: 'Image generation',
    serviceClass: 'A',
    description: 'Generate images from text prompts.',
    providers: [
      // The hosted bond (@molecule/api-ai-image-generation-molecule).
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      { id: 'openai', label: 'OpenAI', envKeys: ['OPENAI_API_KEY'] },
      { id: 'stability', label: 'Stability AI', envKeys: ['STABILITY_API_KEY'] },
    ],
  },
  {
    category: 'speech',
    label: 'Speech / TTS',
    serviceClass: 'A',
    description: 'Text-to-speech and speech-to-text.',
    providers: [
      // The hosted bond (@molecule/api-ai-speech-molecule).
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      { id: 'elevenlabs', label: 'ElevenLabs', envKeys: ['ELEVENLABS_API_KEY'] },
      { id: 'openai', label: 'OpenAI', envKeys: ['OPENAI_API_KEY'] },
    ],
  },
  {
    category: 'translation',
    label: 'Translation',
    serviceClass: 'A',
    description: 'Machine translation between languages.',
    providers: [
      // The hosted bond (@molecule/api-ai-translation-molecule).
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      { id: 'deepl', label: 'DeepL', envKeys: ['DEEPL_API_KEY'] },
    ],
  },
  {
    category: 'ocr',
    label: 'OCR',
    serviceClass: 'A',
    description: 'Extract text from images (scans, screenshots, photos).',
    providers: [
      // The hosted bond (@molecule/api-ocr-molecule). The hosted endpoint
      // serves `ocr/recognize`, so the bond's key belongs here — this comment
      // used to claim "the hosted service needs none", which the registry
      // contradicts: api-ocr-molecule declares MOLECULE_API_KEY required.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      // @molecule/api-ocr-llm recognizes with the app's own AI provider, so
      // this BYO key is a standard OpenAI key.
      { id: 'openai', label: 'OpenAI (vision model)', envKeys: ['OPENAI_API_KEY'] },
    ],
  },
  {
    category: 'web-search',
    label: 'Web search',
    serviceClass: 'A',
    description: 'Search the web for grounding, citations and agent tools.',
    providers: [
      // The hosted bond (@molecule/api-web-search-molecule).
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      // Env keys mirror the registry (@molecule/api-web-search-brave) — a BYO
      // key written under any other name never reaches the package.
      { id: 'brave', label: 'Brave Search', envKeys: ['BRAVE_SEARCH_API_KEY'] },
    ],
  },
  {
    category: 'moderation',
    label: 'Content moderation',
    serviceClass: 'A',
    description: 'Classify text and images for harmful or unsafe content.',
    providers: [
      // The hosted bond (@molecule/api-content-moderation-molecule). The
      // comment here used to claim "the hosted classifier needs no key",
      // which the registry contradicts: that bond declares MOLECULE_API_KEY
      // required.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      // Env keys mirror the registry (@molecule/api-content-moderation-openai)
      // — a BYO key is a standard OpenAI key.
      { id: 'openai', label: 'OpenAI', envKeys: ['OPENAI_API_KEY'] },
    ],
  },
  {
    category: 'maps',
    label: 'Maps / Geocoding',
    serviceClass: 'A',
    description: 'Maps, geocoding, and places lookups.',
    providers: [
      // The hosted bond (@molecule/api-geolocation-molecule) — the hosted
      // endpoint serves `geocoding/geocode` and `geocoding/reverse-geocode`
      // under THIS category (`handler.category === 'maps'`).
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      // Env keys mirror the registry (@molecule/api-geolocation-*) — a BYO key
      // written under any other name never reaches the package.
      { id: 'mapbox', label: 'Mapbox', envKeys: ['MAPBOX_ACCESS_TOKEN'] },
      { id: 'google', label: 'Google Maps', envKeys: ['GOOGLE_MAPS_API_KEY'] },
      // Keyless OSM bonds: their env keys are CONFIGURATION, not credentials —
      // setting one signals the app means to use that provider.
      {
        id: 'nominatim',
        label: 'Nominatim (OpenStreetMap)',
        envKeys: ['NOMINATIM_USER_AGENT'],
      },
      {
        id: 'photon',
        label: 'Photon (OpenStreetMap, self-hosted)',
        envKeys: ['PHOTON_BASE_URL'],
      },
    ],
  },

  {
    category: 'ai-decisions',
    label: 'AI decisions',
    serviceClass: 'A',
    description:
      'Typed AI questions about text or JSON — choice, score, yes/no — with probabilities.',
    providers: [
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      { id: 'llm', label: 'Your own AI bond', envKeys: ['OPENAI_API_KEY', 'ANTHROPIC_API_KEY'] },
    ],
  },
  {
    category: 'image',
    label: 'Image transformation',
    serviceClass: 'A',
    description: 'Resize, crop, rotate, optimize and convert images — self-hosted upstream.',
    providers: [
      // The hosted bond (@molecule/api-image-molecule) reads MOLECULE_API_KEY
      // (its registry secret), so a BYO value points it at your own instance.
      //
      // There is no row for the in-process sharp bond
      // (@molecule/api-image-sharp): that bond is selected in CODE
      // (`bond('image', 'sharp', provider)`) and reads NO environment variable —
      // its registry entry declares no secrets — so an env row could not select
      // it. The `IMAGE_SHARP_ENABLED` row this replaces named a variable nothing
      // in the product reads, so turning the switch on wrote a vault key that
      // changed nothing while the panel reported the category as BYO-configured.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
    ],
  },
  {
    category: 'push-notifications',
    label: 'Push notifications',
    serviceClass: 'A',
    description: 'Send web push notifications to browsers — VAPID handled for you.',
    providers: [
      // The hosted bond (@molecule/api-push-notifications-molecule). The
      // hosted endpoint serves `push/send` and `push/vapid-key` under THIS
      // category, so the key that bond reads belongs here — the comment used
      // to claim "the hosted path needs no key", which the registry
      // contradicts.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      // Env keys mirror the registry (@molecule/api-push-notifications-web-push)
      // — a BYO setup serves its own VAPID identity.
      {
        id: 'web-push',
        label: 'Web Push (VAPID, self-hosted)',
        envKeys: ['VAPID_PUBLIC_KEY', 'VAPID_PRIVATE_KEY', 'VAPID_EMAIL'],
      },
    ],
  },
  {
    category: 'pdf',
    label: 'PDF',
    serviceClass: 'A',
    description: 'Render HTML to PDF, merge PDFs, watermark PDFs — headless Chrome, no vendor.',
    providers: [
      // The hosted PDF services (@molecule/api-pdf-molecule — render, merge,
      // watermark) read MOLECULE_API_KEY, its registry secret: the same row
      // `image` and `ai-decisions` carry, and the same "point it at your own
      // instance" wiring the vault documents for that key.
      //
      // The in-process Chrome bond (@molecule/api-pdf-puppeteer) gets no row:
      // it is configured in code (executablePath, launchArgs) and reads NO
      // environment variable — its registry entry declares no secrets. The
      // `PUPPETEER_EXECUTABLE_PATH` row this replaces named the variable only
      // the PLATFORM's own render machine sets (deploy/fly/pdf-render), never
      // anything the user's app reads, so opting out of brokering wrote a vault
      // key that could not change the app's behaviour.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
    ],
  },
  {
    category: 'screenshots',
    label: 'Screenshots',
    serviceClass: 'A',
    description: 'Capture a URL or HTML to a PNG/JPEG image — headless Chrome, no vendor.',
    providers: [
      // The hosted screenshot service (@molecule/api-screenshots-molecule)
      // reads MOLECULE_API_KEY — the same registry secret row the pdf entry
      // carries. The in-process Chrome bond (@molecule/api-screenshots-
      // puppeteer) is configured in code and reads no env var, like
      // api-pdf-puppeteer above.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
    ],
  },
  {
    category: 'web-content',
    label: 'Web content',
    serviceClass: 'A',
    description: 'Read a page as clean markdown or text — grounded answers, citations, extraction.',
    providers: [
      // The hosted web content service (@molecule/api-web-content-molecule)
      // reads MOLECULE_API_KEY — the same registry secret row the pdf entry
      // carries. The in-process Readability bond (@molecule/api-web-content-
      // readability) is configured in code and reads no env var.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
    ],
  },
  {
    category: 'uptime',
    label: 'Uptime monitoring',
    serviceClass: 'A',
    description:
      'Probe a URL from outside your network — status, latency and TLS expiry; a point-in-time check, driven on your own cadence.',
    providers: [
      // The hosted uptime service (@molecule/api-uptime-molecule) reads
      // MOLECULE_API_KEY — the same registry secret row the other hosted
      // entries carry. The in-process bond (@molecule/api-uptime-fetch)
      // needs no configuration and reads no env var.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
    ],
  },
  {
    category: 'ai-summarization',
    label: 'AI summarization',
    serviceClass: 'A',
    description:
      'Summarize text into a paragraph, bullets or a one-sentence TL;DR — word caps enforced by the service.',
    providers: [
      // The hosted summarization service (@molecule/api-ai-summarization-
      // molecule) reads MOLECULE_API_KEY — the same registry secret row the
      // other hosted entries carry. The LLM bond
      // (@molecule/api-ai-summarization-llm) composes the app's own ai bond
      // and reads no env var of its own.
      { id: 'molecule', label: 'Molecule (hosted)', envKeys: ['MOLECULE_API_KEY'] },
      { id: 'llm', label: 'Your own AI bond', envKeys: ['OPENAI_API_KEY', 'ANTHROPIC_API_KEY'] },
    ],
  },

  // --- Class C: Bring-your-own only ---------------------------------------
  {
    category: 'payments',
    label: 'Payments',
    serviceClass: 'C',
    description: 'Accept payments and manage subscriptions.',
    byoReason:
      'Payouts settle to your own merchant account, so payments always use your own credentials.',
    providers: [
      { id: 'stripe', label: 'Stripe', envKeys: ['STRIPE_SECRET_KEY', 'STRIPE_WEBHOOK_SECRET'] },
    ],
  },
  {
    category: 'oauth',
    label: 'OAuth login',
    serviceClass: 'C',
    description: 'Sign-in with third-party identity providers.',
    byoReason:
      'OAuth apps are registered under your own brand and redirect URIs, so login always uses your own credentials.',
    providers: [
      // Env keys mirror the registry (@molecule/api-oauth-*) — the packages read
      // OAUTH_<PROVIDER>_* exclusively.
      {
        id: 'google',
        label: 'Google',
        envKeys: ['OAUTH_GOOGLE_CLIENT_ID', 'OAUTH_GOOGLE_CLIENT_SECRET'],
      },
      {
        id: 'github',
        label: 'GitHub',
        envKeys: ['OAUTH_GITHUB_CLIENT_ID', 'OAUTH_GITHUB_CLIENT_SECRET'],
      },
    ],
  },
  {
    category: 'kyc',
    label: 'Identity / KYC',
    serviceClass: 'C',
    description: 'Verify customer identity (know-your-customer).',
    byoReason:
      'KYC is bound to your own legal entity and compliance account, so it always uses your own credentials.',
    providers: [
      {
        id: 'stripe-identity',
        label: 'Stripe Identity',
        envKeys: ['STRIPE_SECRET_KEY', 'STRIPE_IDENTITY_WEBHOOK_SECRET'],
      },
    ],
  },
  {
    category: 'esign',
    label: 'E-signature',
    serviceClass: 'C',
    description: 'Collect legally-binding electronic signatures.',
    byoReason:
      'Signed documents are issued under your own account of record, so e-sign always uses your own credentials.',
    providers: [
      // @molecule/api-esign-hellosign (Dropbox Sign, formerly HelloSign) reads
      // HELLOSIGN_API_KEY — the only e-sign provider package today.
      { id: 'dropbox-sign', label: 'Dropbox Sign (HelloSign)', envKeys: ['HELLOSIGN_API_KEY'] },
    ],
  },
] as const

/** Every env key referenced by any provider in the catalog (deduplicated). */
export const CATALOG_ENV_KEYS: readonly string[] = Array.from(
  new Set(SERVICE_CATALOG.flatMap((entry) => entry.providers.flatMap((p) => p.envKeys))),
)
